import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { loadClaimData, saveClaimData, createBackup, listWorkspaces, createWorkspace, deleteWorkspace } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { default526EZ, default0781, conditionLibrary } from "@/lib/claimData";
import { combineRatingsWithBilateral, calculateReadiness, uid } from "@/lib/claimUtils";
import secondaryMap from "@/lib/secondaryConditions";
import AdminView from "@/components/views/AdminView";
import BillingView from "@/components/views/BillingView";
import DashboardView from "@/components/views/DashboardView";
import ConditionsView from "@/components/views/ConditionsView";
import ClaimTrackerView from "@/components/views/ClaimTrackerView";
import FormsView from "@/components/views/FormsView";
import MathView from "@/components/views/MathView";
import FinancialsView from "@/components/views/FinancialsView";
import ExportView from "@/components/views/ExportView";
import PrivacyView from "@/components/views/PrivacyView";
import StatementsView from "@/components/views/StatementsView";
import BackupsView from "@/components/views/BackupsView";
import AppointmentsView from "@/components/views/AppointmentsView";
import PrescriptionsView from "@/components/views/PrescriptionsView";
import SymptomTrackerView from "@/components/views/SymptomTrackerView";
import CalendarSyncView from "@/components/views/CalendarSyncView";
import ImeIntakeView from "@/components/views/ImeIntakeView";
import ShareLinksView from "@/components/views/ShareLinksView";
import PublicShareView from "@/components/views/PublicShareView";
import PasswordResetView from "@/components/views/PasswordResetView";
import ReminderBanner from "@/components/ReminderBanner";
import AuthCallback from "@/components/AuthCallback";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import ToastHost from "@/components/ToastHost";
import CommandPalette from "@/components/CommandPalette";
import PreferencesView from "@/components/views/PreferencesView";
import { initTheme } from "@/lib/theme";
import { initPrefs, getPrefs, toggleFavorite } from "@/lib/prefs";
import { showToast } from "@/lib/toast";

// Clear any stale dark-mode artifact and seed user-density preference before paint.
initTheme();
initPrefs();
import {
  List, X, FloppyDisk, CloudArrowUp, MagnifyingGlass, Star, SlidersHorizontal,
  Plus, Trash, CaretDown, FolderOpen, SignOut, UserCircle, Crown, Bug, EnvelopeSimple,
} from "@phosphor-icons/react";
import { baseNavItems, navGroups, ADMIN_NAV_GROUP, DEFAULTS, VIEWS } from "@/lib/navConfig";
import { THEORIES, CLAIM_TYPES } from "@/lib/claimEnums";
import GuestBanner from "@/components/GuestBanner";
import GuestGate from "@/components/GuestGate";
import AuthPromptModal from "@/components/AuthPromptModal";
import ReportProblemModal from "@/components/ReportProblemModal";
import NewsletterModal from "@/components/NewsletterModal";
import "@/App.css";

function App() {
  // Public share view — render WITHOUT auth, before any hooks.
  // This must be a SIBLING decision, not an embedded early return, to satisfy
  // react-hooks/rules-of-hooks. Splitting into a separate authed component achieves this.
  const sharePathMatch = typeof window !== "undefined" && window.location.pathname.startsWith("/share/")
    ? window.location.pathname.slice(7)
    : null;
  if (sharePathMatch) {
    return <PublicShareView token={sharePathMatch} />;
  }
  // Standalone password-reset page — opened from the link in the reset email.
  // Renders before AuthProvider's /auth/me call so unauthenticated guests don't
  // bounce to the login modal.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/reset")) {
    return <PasswordResetView />;
  }
  // Google OAuth callback — synchronous detection during render (NOT in useEffect)
  // prevents the AuthProvider race that would 401 the /auth/me call mid-exchange.
  // IMPORTANT: this check MUST run before AuthProvider's checkAuth fires, otherwise
  // the loading gate in AuthedApp will trigger a /auth/me request that fails 401.
  // REMINDER: DO NOT HARDCODE THE REDIRECT URL — derive from window.location.origin in LoginView.
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return <AuthedApp />;
}

function AuthedApp() {
  const { user, logout } = useAuth();
  const isGuest = user === null;
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const [reportProblemOpen, setReportProblemOpen] = useState(false);
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [authPromptMode, setAuthPromptMode] = useState("register");
  const openAuthPrompt = useCallback((mode = "register") => {
    setAuthPromptMode(mode);
    setAuthPromptOpen(true);
  }, []);

  // Allow any descendant to open the modal via a custom window event.
  // (Used by toasts/badges that fire from outside the React tree.)
  useEffect(() => {
    function onOpen(e) { openAuthPrompt(e?.detail?.mode || "register"); }
    window.addEventListener("claimops:open-auth", onOpen);
    return () => window.removeEventListener("claimops:open-auth", onOpen);
  }, [openAuthPrompt]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState(() => {
    // Honor the user's saved "default landing view" pref.
    try {
      const raw = localStorage.getItem("claimops:prefs");
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.defaultView || "dashboard";
    } catch { return "dashboard"; }
  });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [prefsTick, setPrefsTick] = useState(0); // bumped to re-read prefs after changes
  const [activeWsId, setActiveWsId] = useState("default");
  const [workspaceList, setWorkspaceList] = useState([]);
  const [wsMenuOpen, setWsMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [workspace, setWorkspace] = useState(DEFAULTS.workspace);
  const [profile, setProfile] = useState(DEFAULTS.profile);
  const [financials, setFinancials] = useState(DEFAULTS.financials);
  const [issues, setIssues] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [master526ez, setMaster526ez] = useState(default526EZ);
  const [form0781, setForm0781] = useState(default0781);
  const [statements, setStatements] = useState([]);
  const [imeIntake, setImeIntake] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [savedAgo, setSavedAgo] = useState("");
  const saveTimer = useRef(null);

  const combined = useMemo(() => combineRatingsWithBilateral(issues), [issues]);
  const readiness = useMemo(() => calculateReadiness(issues, evidence, { workspace, master526ez }), [issues, evidence, workspace, master526ez]);

  // Global ⌘K / Ctrl-K to open the command palette + listen for pref changes so
  // the sidebar re-reads favorites when the user pins/unpins from anywhere.
  useEffect(() => {
    function onKey(e) {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const cmd = isMac ? e.metaKey : e.ctrlKey;
      if (cmd && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    }
    function onPrefsChange() { setPrefsTick((t) => t + 1); }
    window.addEventListener("keydown", onKey);
    window.addEventListener("prefschange", onPrefsChange);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("prefschange", onPrefsChange);
    };
  }, []);

  const prefs = useMemo(() => getPrefs(), [prefsTick]);
  const favorites = prefs.favorites || [];
  const hidden = prefs.hidden || [];

  const isAdmin = user?.role === "admin";
  const navSectionsBase = useMemo(() => (
    isAdmin ? [...navGroups, ADMIN_NAV_GROUP] : navGroups
  ), [isAdmin]);
  // Apply user preferences: hide items the user has hidden, and surface
  // favorites as a "Pinned" group at the top.
  const navSections = useMemo(() => {
    const allItems = navSectionsBase.flatMap((s) => s.items);
    const byId = Object.fromEntries(allItems.map((it) => [it.id, it]));
    const pinned = favorites
      .map((id) => byId[id])
      .filter(Boolean);
    const visibleSections = navSectionsBase.map((s) => ({
      ...s,
      items: s.items.filter((it) => !hidden.includes(it.id) && !favorites.includes(it.id)),
    })).filter((s) => s.items.length > 0);
    if (pinned.length === 0) return visibleSections;
    return [{ id: "pinned", label: "Pinned", items: pinned }, ...visibleSections];
  }, [navSectionsBase, favorites, hidden]);
  void baseNavItems;
  const sortedWorkspaceList = useMemo(
    () => [...workspaceList].sort((a, b) => (a.title || "").localeCompare(b.title || "")),
    [workspaceList],
  );

  const loadWorkspace = useCallback(async (wsId) => {
    setLoading(true);
    const data = await loadClaimData(wsId);
    if (data) {
      setWorkspace(data.workspace?.title ? data.workspace : DEFAULTS.workspace);
      setProfile(data.profile?.name ? data.profile : DEFAULTS.profile);
      setFinancials(data.financials && Object.keys(data.financials).length ? { ...DEFAULTS.financials, ...data.financials } : DEFAULTS.financials);
      setIssues(data.issues?.length ? data.issues : []);
      setEvidence(data.evidence?.length ? data.evidence : []);
      if (data.master526ez?.veteranName || data.master526ez?.claimedConditions?.length) {
        setMaster526ez({ ...default526EZ, ...data.master526ez });
      } else { setMaster526ez(default526EZ); }
      if (data.form0781?.veteranName || data.form0781?.eventDescription) {
        setForm0781({ ...default0781, ...data.form0781 });
      } else { setForm0781(default0781); }
      setStatements(data.statements?.length ? data.statements : []);
      setImeIntake(data.imeIntake && typeof data.imeIntake === "object" ? data.imeIntake : {});
    } else {
      setWorkspace(DEFAULTS.workspace); setProfile(DEFAULTS.profile); setFinancials(DEFAULTS.financials);
      setIssues([]); setEvidence([]); setMaster526ez(default526EZ); setForm0781(default0781); setStatements([]);
      setImeIntake({});
    }
    setActiveWsId(wsId);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user === undefined) return; // still verifying — wait
    (async () => {
      if (user) {
        const wsList = await listWorkspaces();
        setWorkspaceList(wsList);
        await loadWorkspace("default");
      } else {
        // Guest: hydrate from localStorage; no server fetch, no workspace list.
        setWorkspaceList([]);
        await loadWorkspace("default");
      }
    })();
  }, [loadWorkspace, user]);

  const currentSnapshot = useMemo(() => ({
    workspace, profile, financials, issues, evidence, master526ez, form0781, statements, imeIntake,
  }), [workspace, profile, financials, issues, evidence, master526ez, form0781, statements, imeIntake]);

  // True iff a snapshot has no meaningful user-entered claim data. We treat
  // the workspace/profile/financials/forms as scaffolding (defaults are
  // populated on mount) and check only the user-authored collections plus
  // any data the veteran actually typed into the 526EZ/0781 forms.
  // Used to guard against the guest→auth migration accidentally saving an
  // empty in-memory snapshot OVER the server's populated data.
  const isSnapshotEffectivelyEmpty = useCallback((snap) => {
    if (!snap) return true;
    if ((snap.issues || []).length > 0) return false;
    if ((snap.evidence || []).length > 0) return false;
    if ((snap.statements || []).length > 0) return false;
    if (snap.master526ez?.veteranName) return false;
    if ((snap.master526ez?.claimedConditions || []).length > 0) return false;
    if (snap.form0781?.eventDescription) return false;
    return true;
  }, []);

  // When a guest signs in/up, migrate their in-memory workspace to the server
  // so the data they entered during the trial doesn't disappear after auth.
  // CRITICAL: skip the migration save when the guest snapshot is essentially
  // empty — otherwise we race against loadWorkspace() and can clobber the
  // server's populated data with our empty defaults.
  const prevUserRef = useRef(user);
  useEffect(() => {
    const wasGuest = prevUserRef.current === null;
    const nowAuthed = !!user;
    if (wasGuest && nowAuthed) {
      // Snapshot the migration source ONCE, synchronously, before any awaits.
      const migrationSnapshot = currentSnapshot;
      if (isSnapshotEffectivelyEmpty(migrationSnapshot)) {
        // Nothing meaningful to migrate — skip the save entirely. loadWorkspace
        // will fetch and display whatever's already on the server.
        prevUserRef.current = user;
        return;
      }
      (async () => {
        try {
          await saveClaimData(migrationSnapshot, "default");
          await createBackup("Saved from trial mode", migrationSnapshot);
          showToast({ variant: "success", message: "Your trial work is now saved to your account." });
        } catch (err) {
          console.warn("guest→auth migration save failed", err);
        }
      })();
    }
    prevUserRef.current = user;
    // currentSnapshot intentionally referenced only at the moment of transition;
    // omitting it from deps avoids re-saving on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const debouncedSave = useCallback((snap, wsId) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveClaimData(snap, wsId);
      setSaveStatus("saved");
      setLastSavedAt(new Date());
      setTimeout(() => setSaveStatus(""), 2000);
    }, 1500);
  }, [saveTimer]);

  // Once loadWorkspace finishes for an authed user we record the FIRST
  // post-load snapshot. Auto-save is gated until we've seen that snapshot —
  // this prevents the (loading=false but state still empty defaults) window
  // from triggering a debounced save that would clobber server data.
  const initialAuthedLoadComplete = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Guests: localStorage hydration is synchronous, safe to save freely.
      debouncedSave(currentSnapshot, activeWsId);
      return;
    }
    if (!initialAuthedLoadComplete.current) {
      // First (loading=false) tick after server fetch — mark complete but
      // DO NOT save this snapshot. It's identical to what the server already
      // has, and persisting it before the user has interacted just risks
      // overwriting a concurrent change from another tab.
      initialAuthedLoadComplete.current = true;
      return;
    }
    debouncedSave(currentSnapshot, activeWsId);
  }, [currentSnapshot, loading, debouncedSave, activeWsId, user]);

  // Re-arm the gate whenever the user signs out / switches workspace so a
  // future load doesn't immediately re-save the freshly fetched data.
  useEffect(() => { initialAuthedLoadComplete.current = false; }, [user, activeWsId]);


  // Tick the "saved Xs ago" label so users get a live, Google-Docs-style indicator.
  useEffect(() => {
    if (!lastSavedAt) { setSavedAgo(""); return; }
    function format() {
      const secs = Math.max(0, Math.floor((Date.now() - lastSavedAt.getTime()) / 1000));
      if (secs < 5) return "Saved just now";
      if (secs < 60) return `Saved ${secs}s ago`;
      const mins = Math.floor(secs / 60);
      if (mins < 60) return `Saved ${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      return `Saved ${hrs}h ago`;
    }
    setSavedAgo(format());
    const t = setInterval(() => setSavedAgo(format()), 15000);
    return () => clearInterval(t);
  }, [lastSavedAt]);

  const manualSave = async () => {
    if (isGuest) {
      openAuthPrompt("register");
      return;
    }
    setSaveStatus("saving");
    await createBackup("Manual save", currentSnapshot);
    await saveClaimData(currentSnapshot, activeWsId);
    setSaveStatus("saved");
    setLastSavedAt(new Date());
    showToast({ variant: "success", message: "Saved and backed up." });
    setTimeout(() => setSaveStatus(""), 2500);
  };

  const updateIssue = (id, patch) => setIssues((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  const deleteIssue = (id) => {
    let removedIssue = null;
    let removedEvidence = [];
    setIssues((prev) => {
      removedIssue = prev.find((i) => i.id === id) || null;
      return prev.filter((i) => i.id !== id);
    });
    setEvidence((prev) => {
      removedEvidence = prev.filter((ev) => (ev.issueIds || []).includes(id) && ev.issueIds.length === 1);
      return prev
        .map((ev) => ({ ...ev, issueIds: (ev.issueIds || []).filter((eid) => eid !== id) }))
        .filter((ev) => ev.issueIds.length > 0);
    });
    if (removedIssue) {
      showToast({
        variant: "info",
        duration: 6000,
        message: `Removed "${removedIssue.claimedAs || "Condition"}" from the claim.`,
        action: {
          label: "UNDO",
          onClick: () => {
            setIssues((prev) => [removedIssue, ...prev]);
            if (removedEvidence.length) setEvidence((prev) => [...removedEvidence, ...prev]);
          },
        },
      });
    }
  };
  const deleteEvidence = (id) => setEvidence((prev) => prev.filter((ev) => ev.id !== id));

  const addCondition = (condition) => {
    if (condition.tag === "not-ratable") {
      const ok = window.confirm(
        `${condition.name} is generally NOT service-connectable per 38 CFR 3.303(c) (congenital or developmental defect).\n\n` +
        `Adding it will not improve your rating unless you can show in-service aggravation by a superimposed disease/injury. Add anyway?`,
      );
      if (!ok) return;
    }
    const isSecondaryOnly = condition.tag === "secondary-only";
    const theory = isSecondaryOnly
      ? THEORIES.secondary
      : THEORIES.direct;
    const newIssue = {
      id: `issue-${uid()}`,
      claimedAs: condition.name,
      code: condition.code,
      system: condition.system,
      theory,
      priority: "Medium",
      estimatedRating: 0,
      targetRating: 0,
      evidence: {},
    };
    setIssues((prev) => [newIssue, ...prev]);
    setMaster526ez((prev) => ({
      ...prev,
      claimedConditions: [
        ...(prev.claimedConditions || []),
        { issueId: newIssue.id, claimedAs: condition.name, diagnosticReference: condition.code, theory },
      ],
    }));
    // Nudge: surface commonly co-claimed conditions immediately after the user adds one.
    // We compute against the *previous* issues list (newIssue is the focal point) so the toast
    // only fires when there's at least one real co-occurrence hint to show.
    const coHints = secondaryMap[condition.code] || [];
    const existingCodes = new Set([...issues, newIssue].map((i) => i.code));
    const fresh = coHints.filter((s) => !existingCodes.has(s.code));
    if (fresh.length > 0) {
      showToast({
        variant: "info",
        duration: 7000,
        message: `Added "${condition.name}". ${fresh.length} condition${fresh.length === 1 ? " is" : "s are"} commonly co-claimed.`,
        action: {
          label: "VIEW",
          onClick: () => setActiveView(VIEWS.dashboard),
        },
      });
    }
  };

  // Shared between Claim Tracker and the Command Center co-occurrence panel.
  const addSecondary = (suggestion, primaryIssue) => {
    const match = conditionLibrary.find((c) => c.code === suggestion.code);
    const newIssue = {
      id: `issue-${uid()}`,
      claimedAs: suggestion.name,
      code: suggestion.code,
      system: suggestion.system || match?.system || "musculoskeletal",
      theory: THEORIES.secondary,
      priority: "Medium",
      estimatedRating: 0,
      targetRating: 0,
      functionalImpact: "",
      secondaryTo: primaryIssue.claimedAs,
      secondaryReason: suggestion.reason,
      evidence: {},
    };
    setIssues((prev) => [newIssue, ...prev]);
    setMaster526ez((prev) => ({
      ...prev,
      claimedConditions: [...(prev.claimedConditions || []), { issueId: newIssue.id, claimedAs: suggestion.name, diagnosticReference: suggestion.code, theory: THEORIES.secondary }],
    }));
  };

  async function handleCreateWorkspace() {
    try {
      const ws = await createWorkspace("New Workspace", CLAIM_TYPES.bdd);
      if (ws) {
        setWorkspaceList((prev) => [ws, ...prev]);
        await loadWorkspace(ws.id);
        setWsMenuOpen(false);
      }
    } catch (err) {
      if (err.code === 402) {
        setWsMenuOpen(false);
        setActiveView(VIEWS.billing);
        return;
      }
      console.error(err);
    }
  }

  async function handleDeleteWorkspace(wsId) {
    if (!window.confirm("Delete this workspace and all its data?")) return;
    await deleteWorkspace(wsId);
    setWorkspaceList((prev) => prev.filter((w) => w.id !== wsId));
    if (activeWsId === wsId) await loadWorkspace("default");
  }

  async function switchWorkspace(wsId) {
    await saveClaimData(currentSnapshot, activeWsId);
    await loadWorkspace(wsId);
    setWsMenuOpen(false);
  }

  // Import functionality removed per user request.

  // Auth gate: while AuthContext is verifying, show splash. Guests (user === null)
  // get the full app surface in trial mode — saves prompt for sign-in instead.
  if (user === undefined) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <div className="loading-logo">VA</div>
        <div className="loading-text">CLAIMOPS VA</div>
        <div className="loading-sub">Authenticating...</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <div className="loading-logo">VA</div>
        <div className="loading-text">CLAIMOPS VA</div>
        <div className="loading-sub">Loading claim data...</div>
      </div>
    );
  }

  return (
    <div className="app-shell" data-testid="app-shell">
      <div className="mobile-header" data-testid="mobile-header">
        <button onClick={() => setMobileOpen(!mobileOpen)} className="mobile-menu-btn" data-testid="mobile-menu-btn">
          {mobileOpen ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
        </button>
        <div className="mobile-title-wrap">
          <span className="mobile-title">ClaimOps</span>
          <span className="mobile-subtitle">{workspace.claimType || "Claim Workspace"}</span>
        </div>
        <button
          onClick={() => setActiveView(VIEWS.forms)}
          className="mobile-export-btn"
          data-testid="mobile-export-packet-btn"
          title="Export claim packet"
        >
          <span className="mobile-export-label">Export Packet</span>
        </button>
      </div>

      <aside className={`sidebar ${mobileOpen ? "sidebar--open" : ""}`} data-testid="sidebar">
        <div className="sidebar-brand" data-testid="sidebar-brand">
          <div className="brand-mark">VA</div>
          <div>
            <div className="brand-name">CLAIMOPS</div>
            <div className="brand-sub">Claim Operating System</div>
          </div>
        </div>

        {/* Workspace Selector — authed only; guests only have one local workspace */}
        {!isGuest && (
          <div className="ws-selector" data-testid="ws-selector">
            <button className="ws-current" onClick={() => setWsMenuOpen(!wsMenuOpen)} data-testid="ws-current-btn">
              <FolderOpen size={14} weight="bold" />
              <span className="ws-current-name">{activeWsId === "default" ? "Default Workspace" : workspace.title}</span>
              <CaretDown size={12} weight="bold" />
            </button>
            {wsMenuOpen && (
              <div className="ws-menu" data-testid="ws-menu">
                <button className={`ws-menu-item ${activeWsId === "default" ? "ws-menu-item--active" : ""}`} onClick={() => switchWorkspace("default")} data-testid="ws-default">
                  Default Workspace
                </button>
                {sortedWorkspaceList.map((ws) => (
                  <div key={ws.id} className="ws-menu-row" data-testid={`ws-item-${ws.id}`}>
                    <button className={`ws-menu-item ${activeWsId === ws.id ? "ws-menu-item--active" : ""}`} onClick={() => switchWorkspace(ws.id)}>
                      {ws.title}
                    </button>
                    <button className="ws-del-btn" onClick={() => handleDeleteWorkspace(ws.id)} data-testid={`ws-del-${ws.id}`}>
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                ))}
                <button className="ws-menu-add" onClick={handleCreateWorkspace} data-testid="ws-create-btn">
                  <Plus size={12} weight="bold" /> New Workspace
                </button>
              </div>
            )}
          </div>
        )}
        {isGuest && (
          <div className="ws-selector ws-selector--guest" data-testid="ws-guest-pill">
            <FolderOpen size={14} weight="bold" />
            <span className="ws-current-name">Trial workspace</span>
            <span className="ws-guest-badge">LOCAL</span>
          </div>
        )}

        <nav className="sidebar-nav" data-testid="sidebar-nav">
          {navSections.map((section) => (
            <div key={section.id} className="nav-section" data-testid={`nav-section-${section.id}`}>
              <div className="nav-section-label">{section.label}</div>
              {section.items.map((item) => {
                const Icon = item.icon;
                const isFav = favorites.includes(item.id);
                return (
                  <div
                    key={item.id}
                    className={`nav-item ${activeView === item.id ? "nav-item--active" : ""}`}
                    data-testid={`nav-${item.id}`}
                  >
                    <button
                      type="button"
                      className="nav-item-main"
                      onClick={() => { setActiveView(item.id); setMobileOpen(false); }}
                    >
                      <Icon size={17} weight={activeView === item.id ? "fill" : "regular"} />
                      <span>{item.label}</span>
                    </button>
                    <button
                      type="button"
                      className="nav-item-fav"
                      aria-label={isFav ? "Unpin from favorites" : "Pin to favorites"}
                      onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id); }}
                      data-testid={`nav-fav-${item.id}`}
                    >
                      <Star size={13} weight={isFav ? "fill" : "regular"} />
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer" data-testid="sidebar-footer">
          <button onClick={manualSave} className="save-btn" data-testid="save-btn">
            {saveStatus === "saving" ? <CloudArrowUp size={16} weight="bold" /> : <FloppyDisk size={16} weight="bold" />}
            <span>{saveStatus === "saving" ? "Saving..." : (isGuest ? "Sign in to save" : (savedAgo || "Save"))}</span>
          </button>
          <button
            type="button"
            className="sidebar-report-btn"
            onClick={() => setReportProblemOpen(true)}
            data-testid="report-problem-btn"
          >
            <Bug size={14} weight="bold" />
            <span>Report a problem</span>
          </button>
          <button
            type="button"
            className="sidebar-report-btn sidebar-newsletter-btn"
            onClick={() => setNewsletterOpen(true)}
            data-testid="newsletter-btn"
          >
            <EnvelopeSimple size={14} weight="bold" />
            <span>Get VA news & updates</span>
          </button>
          {isGuest ? (
            <div className="user-menu-wrap" data-testid="guest-cta-wrap">
              <button
                className="user-menu-btn user-menu-btn--guest"
                onClick={() => openAuthPrompt("register")}
                data-testid="guest-signin-btn"
              >
                <UserCircle size={18} weight="bold" />
                <span className="user-menu-email">Sign in / Create account</span>
              </button>
            </div>
          ) : (
            <div className="user-menu-wrap" data-testid="user-menu-wrap">
              <button className="user-menu-btn" onClick={() => setUserMenuOpen(!userMenuOpen)} data-testid="user-menu-btn">
                <UserCircle size={18} weight="bold" />
                <span className="user-menu-email" title={user.email}>{user.email}</span>
                {isAdmin && <span className="user-admin-pill">ADMIN</span>}
                <CaretDown size={10} weight="bold" />
              </button>
              {userMenuOpen && (
                <div className="user-menu" data-testid="user-menu">
                  <div className="user-menu-info">
                    <div className="user-menu-info-label">SIGNED IN AS</div>
                    <div className="user-menu-info-email">{user.email}</div>
                    <div className="user-menu-info-plan">
                      {user.is_pro ? (
                        <span className="user-menu-pro"><Crown size={10} weight="fill" /> PRO PLAN</span>
                      ) : (
                        <span className="user-menu-free">FREE PLAN</span>
                      )}
                    </div>
                  </div>
                  {!user.is_pro && (
                    <button className="user-menu-item user-menu-item--upgrade" onClick={() => { setUserMenuOpen(false); setActiveView(VIEWS.billing); }} data-testid="upgrade-menu-btn">
                      <Crown size={14} weight="bold" /> Upgrade to Pro
                    </button>
                  )}
                  <button className="user-menu-item" onClick={() => { setUserMenuOpen(false); setActiveView(VIEWS.billing); }} data-testid="billing-menu-btn">
                    <Crown size={14} weight="bold" /> Plan & Billing
                  </button>
                  <button className="user-menu-item" onClick={() => { setUserMenuOpen(false); setActiveView(VIEWS.preferences); }} data-testid="preferences-menu-btn">
                    <SlidersHorizontal size={14} weight="bold" /> Preferences
                  </button>
                  <button className="user-menu-item user-menu-item--danger" onClick={logout} data-testid="logout-btn">
                    <SignOut size={14} weight="bold" /> Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>

      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

      <main className="main-content" data-testid="main-content">
        <header className="content-header" data-testid="content-header">
          <div>
            <div className="header-label">CLAIMOPS VA</div>
            <h1 className="header-title" data-testid="workspace-title">{workspace.title}</h1>
          </div>
          <button
            type="button"
            className="header-search"
            onClick={() => setPaletteOpen(true)}
            data-testid="header-search-btn"
            aria-label="Open search and command palette"
          >
            <MagnifyingGlass size={15} weight="bold" />
            <span className="header-search-placeholder">Search views, forms, statements...</span>
            <kbd className="header-search-kbd">⌘K</kbd>
          </button>
          <div className="header-pills">
            <span className="pill" data-testid="claim-type-pill">{workspace.claimType}</span>
          </div>
        </header>

        <div className="content-area" data-testid="content-area">
          {isGuest && <GuestBanner onSignIn={() => openAuthPrompt("register")} />}
          <VerifyEmailBanner />
          {activeView === VIEWS.dashboard && <ReminderBanner evidence={evidence} setActiveView={setActiveView} />}
          {activeView === VIEWS.dashboard && <DashboardView workspace={workspace} setWorkspace={setWorkspace} profile={profile} setProfile={setProfile} readiness={readiness} combined={combined} setActiveView={setActiveView} issues={issues} evidence={evidence} financials={financials} addSecondary={addSecondary} master526ez={master526ez} statements={statements} isGuest={isGuest} onSignIn={() => openAuthPrompt("register")} />}
          {activeView === VIEWS.conditions && <ConditionsView addCondition={addCondition} issues={issues} setActiveView={setActiveView} />}
          {activeView === VIEWS.tracker && <ClaimTrackerView issues={issues} updateIssue={updateIssue} deleteIssue={deleteIssue} evidence={evidence} setEvidence={setEvidence} deleteEvidence={deleteEvidence} statements={statements} addSecondary={addSecondary} setActiveView={setActiveView} workspace={workspace} master526ez={master526ez} />}
          {activeView === VIEWS.forms && <FormsView master526ez={master526ez} setMaster526ez={setMaster526ez} form0781={form0781} setForm0781={setForm0781} issues={issues} evidence={evidence} statements={statements} profile={profile} workspace={workspace} />}
          {activeView === VIEWS.statements && <StatementsView issues={issues} evidence={evidence} statements={statements} setStatements={setStatements} />}
          {activeView === VIEWS.appointments && (isGuest
            ? <GuestGate testId="guest-gate-appointments" title="Appointments are part of your saved claim file" description="Track every C&P exam, VA visit, and civilian follow-up with reminders and one-click .ics calendar exports — but it lives in your account." bullets={["Auto-link appointments to claimed conditions", "N-day-before reminders + ICS export", "One-tap Day-of-Exam Prep packet PDF"]} onSignIn={() => openAuthPrompt("register")} />
            : <AppointmentsView issues={issues} evidence={evidence} profile={profile} workspaceTitle={workspace?.title} workspace={workspace} master526ez={master526ez} isFree={!user?.is_pro} />)}
          {activeView === VIEWS.prescriptions && (isGuest
            ? <GuestGate testId="guest-gate-prescriptions" title="Prescriptions stay with your account" description="Scan a bottle, log refills, and get a 7-day-out alert before you run out — but the data is per-account so you can sync across devices." bullets={["Photo OCR + barcode scanner on the bottle label", "Smart next-refill calc with alerts", "One-tap copy of pharmacy + Rx number"]} onSignIn={() => openAuthPrompt("register")} />
            : <PrescriptionsView />)}
          {activeView === VIEWS.symptoms && (isGuest
            ? <GuestGate testId="guest-gate-symptoms" title="Symptom Tracker requires an account" description="Daily logs and flares are the heart of a C&P prep packet — they need a private, persistent home tied to your account." bullets={["VA C&P symptom taxonomy across 13 body systems", "30-day severity & mood trend visualizations", "Printable Symptom Log PDF with sparklines"]} onSignIn={() => openAuthPrompt("register")} />
            : <SymptomTrackerView issues={issues} workspace={workspace} profile={profile} master526ez={master526ez} isFree={!user?.is_pro} setActiveView={setActiveView} />)}
          {activeView === VIEWS.calendarSync && (isGuest
            ? <GuestGate testId="guest-gate-calendar" title="Calendar Sync needs an account" description="Subscribe Google / Apple / Outlook to your private ClaimOps feed so every appointment and refill shows up automatically." bullets={["RFC 5545 ICS feed with rotating private token", "Per-event .ics download fallback", "Privacy-first: no Google OAuth required"]} onSignIn={() => openAuthPrompt("register")} />
            : <CalendarSyncView />)}
          {activeView === VIEWS.ime && <ImeIntakeView profile={profile} master526ez={master526ez} issues={issues} evidence={evidence} statements={statements} imeIntake={imeIntake} setImeIntake={setImeIntake} />}
          {activeView === VIEWS.shares && (isGuest
            ? <GuestGate testId="guest-gate-shares" title="Share Links require an account" description="Create read-only links so a VSO, attorney, or family member can review your claim file without you exporting a PDF." bullets={["Per-link revocation and expiry", "No PII leakage (email, billing info hidden)", "Access counter to see who opened it"]} onSignIn={() => openAuthPrompt("register")} />
            : <ShareLinksView />)}
          {activeView === VIEWS.math && <MathView issues={issues} updateIssue={updateIssue} combined={combined} />}
          {activeView === VIEWS.financials && <FinancialsView profile={profile} setProfile={setProfile} financials={financials} setFinancials={setFinancials} combined={combined} />}
          {activeView === VIEWS.export && <ExportView workspace={workspace} profile={profile} financials={financials} issues={issues} evidence={evidence} combined={combined} master526ez={master526ez} form0781={form0781} statements={statements} currentSnapshot={currentSnapshot} isPro={user?.is_pro} setActiveView={setActiveView} />}
          {activeView === VIEWS.backups && (isGuest
            ? <GuestGate testId="guest-gate-backups" title="Backups live in your account" description="Every save creates a backup you can restore from later. Without an account there's nothing on the server to roll back to." bullets={["Unlimited backups on Pro", "One-click restore to any prior state", "Manual save + auto snapshot triggers"]} onSignIn={() => openAuthPrompt("register")} />
            : <BackupsView currentSnapshot={currentSnapshot} onRestore={() => window.location.reload()} />)}
          {activeView === VIEWS.billing && (isGuest
            ? <GuestGate testId="guest-gate-billing" title="Sign in to manage billing" description="Plan management, invoices, and subscription updates require a ClaimOps account." bullets={["Free tier: 1 workspace, watermarked PDFs", "Pro tier: unlimited workspaces, backups, calendar sync", "Cancel anytime"]} onSignIn={() => openAuthPrompt("register")} />
            : <BillingView />)}
          {activeView === VIEWS.privacy && <PrivacyView />}
          {activeView === VIEWS.preferences && <PreferencesView />}
          {activeView === VIEWS.admin && isAdmin && <AdminView />}
        </div>
      </main>
      <ToastHost />
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={(id) => setActiveView(id)}
        isAdmin={isAdmin}
      />
      <AuthPromptModal
        open={authPromptOpen}
        onClose={() => setAuthPromptOpen(false)}
        defaultMode={authPromptMode}
      />
      <ReportProblemModal
        open={reportProblemOpen}
        onClose={() => setReportProblemOpen(false)}
        currentView={activeView}
      />
      <NewsletterModal
        open={newsletterOpen}
        onClose={() => setNewsletterOpen(false)}
        source="sidebar-pin"
      />
    </div>
  );
}

export default App;
